import { useState, useRef, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  useLocationState,
  useReceiveExternalMessages,
} from "./useLocationState";
import useSetData from "./useSetData";
import useLookupNavigation from "./useLookupNavigation";
import { PRICE_SCHEDULE_MODES } from "../../utils/Constants";
import * as utilFunctions from "../../utils/UtilFunctions";

const useCrossAppSlices = ({
  getDefaultInitialParamsData = null,
  mode = "",
  recordsKey = "",
}) => {
  const location = useLocation();
  const { previousState, currentState, setStateVal } = useLocationState({
    initialState: location.state ?? [],
  });

  const {
    currentIndex: initialIndex = 0,
    paramsData: initialParamsData = getDefaultInitialParamsData?.(
      currentState,
    ) || {},
    lookupScreen,
    lookupParams,
    navigateData,
    lookupMode: initialLookupMode = false,
    formData: baseFormData = {},
    currentMode: initialCurrentMode = "",
    dataDrafted: initialDataDrafted = false,
    externalMode: initialExternalMode = false,
  } = currentState;

  const { paramsData, setParamsData } = useSetData({ initialParamsData });
  const [currentMode, setCurrentMode] = useState(initialCurrentMode || mode);
  const [externalMode, setExternalMode] = useState(initialExternalMode);
  const isBaseFormDataExists = Object.keys(baseFormData).length > 0;
  const isFormDataInitialisedRef = useRef(isBaseFormDataExists);
  const isSkippedEqualityCheckRef = useRef(initialDataDrafted);
  const [initialFormData, setInitialFormData] = useState(() =>
    isBaseFormDataExists ? { ...baseFormData } : {},
  );

  const {
    lookupData,
    fetchLookupData,
    viewLookupDetails,
    lookupMode,
    currentIndex,
    setCurrentIndex,
  } = useLookupNavigation({
    initialFormData,
    initialIndex,
    initialLookupMode,
    previousState,
    currentState: {
      ...currentState,
      paramsData,
    },
    navigateData,
    lookupScreen,
    lookupParams,
    currentMode,
    extraFrescheParams: {
      initialFrescheParamsData: paramsData,
    },
    setStateVal,
    externalMode,
  });

  useReceiveExternalMessages(
    () => {},
    () => {},
    (data, message = "") => {
      if (
        message === "navigate-to-ai-new-program" &&
        Object.keys(data ?? {}).length
      ) {
        const {
          message = "",
          programCode = "",
          parameters: {
            screenParams = {},
            mediatorId = "",
            returnProgramCode = "",
          } = {},
        } = data;
        setParamsData(screenParams);
        setCurrentIndex(0);
        return setStateVal([
          {
            message,
            programCode,
            returnProgramCode,
            [recordsKey]: [screenParams],
            mediatorId,
          },
        ]);
      }
      const currentStateData = data[data.length - 1];
      setCurrentIndex(currentStateData?.currentIndex);

      if (currentStateData?.isExternalMode) {
        setStateVal(data);
        setExternalMode(true);
        return;
      }
      setCurrentMode(currentStateData?.currentMode);
      const isFrescheImplementation =
        currentStateData?.isFrescheImplementation || false;
      if (
        currentStateData?.paramsData ||
        currentStateData?.initialFrescheParamsData
      ) {
        setParamsData(
          isFrescheImplementation
            ? currentStateData?.initialFrescheParamsData
            : currentStateData?.paramsData,
        );
      }
      if (Object.keys(currentStateData?.formData).length) {
        const frescheFormData = isFrescheImplementation && {
          ...currentStateData?.formData,
          ...currentStateData?.frescheLookupParams?.reduce((acc, item) => {
            let legacyValue = currentStateData?.paramsData?.[item["legacy"]];
            if (legacyValue && item.formatter) {
              if (
                item.formatter === "formatDateString" &&
                legacyValue.includes("-")
              )
                legacyValue = utilFunctions?.[item.formatter]?.(legacyValue);
            }
            acc[item["source"]] =
              legacyValue || currentStateData?.formData[item["source"]];
            return acc;
          }, {}),
        };
        setInitialFormData(
          !isFrescheImplementation
            ? currentStateData?.formData
            : frescheFormData,
        );
        isFormDataInitialisedRef.current = true;
        isSkippedEqualityCheckRef.current =
          !isFrescheImplementation || currentStateData?.lookupMode
            ? currentStateData?.dataDrafted
            : JSON.stringify(frescheFormData) !==
                JSON.stringify(currentStateData?.formData) ||
              currentStateData?.dataDrafted;

        currentStateData.formData = {};
        currentStateData.isFrescheImplementation = false;
        setStateVal(data);
      }
    },
  );

  const resetFormRef = useCallback((refType = "isFormData") => {
    if (refType === "isFormData" && isFormDataInitialisedRef.current)
      isFormDataInitialisedRef.current = false;
    if (refType === "isSkippedCheck" && isSkippedEqualityCheckRef.current)
      isSkippedEqualityCheckRef.current = false;
  }, []);

  const setHandleBack = useCallback(() => {
    setCurrentIndex((prev) => prev + 1);
    setCurrentMode(mode);
    setInitialFormData({});
    resetFormRef();
  }, []);

  const setViewEditHandler = useCallback((btn = "View") => {
    setCurrentMode(
      btn === "Edit"
        ? PRICE_SCHEDULE_MODES.EDIT
        : PRICE_SCHEDULE_MODES.READ_ONLY,
    );
    currentState.lookupMode = false;
    setInitialFormData({});
    resetFormRef();
  }, []);

  const onFetchInitialData = useCallback(
    (fetchInitialData) => {
      if (
        !isFormDataInitialisedRef.current &&
        !Object.keys(initialFormData).length
      )
        fetchInitialData?.();
    },
    [initialFormData, externalMode],
  );

  const isNoChangeOfInfo = useCallback(
    (dataForSubmission) =>
      !isSkippedEqualityCheckRef.current &&
      JSON.stringify(dataForSubmission) === JSON.stringify(initialFormData),
    [initialFormData],
  );

  useEffect(() => {
    if (!lookupMode) resetFormRef();
  }, [lookupMode, currentMode]);

  return {
    currentState,
    previousState,
    setStateVal,
    paramsData,
    currentMode,
    setCurrentMode,
    initialFormData,
    setInitialFormData,
    lookupData,
    fetchLookupData,
    viewLookupDetails,
    currentIndex,
    setCurrentIndex,
    resetFormRef,
    setHandleBack,
    setViewEditHandler,
    onFetchInitialData,
    isNoChangeOfInfo,
    location,
    externalMode,
  };
};

export default useCrossAppSlices;